import { z } from "zod";

import {
  INTERACTION_TIME_UNITS,
  type InteractionTimeUnit,
  type InteractionValue,
  type InteractionValueType,
  type SerializedInteractionValue,
} from "./interactionTypes";

/** An integer in its one decimal spelling: no leading zeros, no plus sign, and no `-0`. */
export const integerStringSchema = z.string().regex(/^(?:0|-?[1-9]\d*)$/);
const timeUnitSchema = z.enum(INTERACTION_TIME_UNITS);
const provenanceSchema = z.strictObject({
  topicId: z.string().min(1),
  partitionId: z.string().min(1),
  timelineSource: z.string().min(1),
  offsetNs: integerStringSchema,
});

export const interactionValueTypeSchema = z.union([
  z.strictObject({
    type: z.enum(["string", "boolean", "number", "integer"]),
    nullable: z.boolean().optional(),
  }),
  z.strictObject({
    type: z.literal("instant"),
    unit: timeUnitSchema,
    epoch: z.string().min(1),
    nullable: z.boolean().optional(),
  }),
]);

export const serializedInteractionValueSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("null") }),
  z.strictObject({ type: z.literal("string"), value: z.string() }),
  z.strictObject({ type: z.literal("boolean"), value: z.boolean() }),
  z.strictObject({ type: z.literal("number"), value: z.number() }),
  z.strictObject({ type: z.literal("integer"), value: integerStringSchema }),
  z.strictObject({
    type: z.literal("instant"),
    ticks: integerStringSchema,
    unit: timeUnitSchema,
    epoch: z.string().min(1),
    provenance: provenanceSchema.optional(),
  }),
]);

/**
 * A value, or why there is none. `message` is written for the chat, which reads the board's JSON and so may name its
 * tagged values; `forPeople`, where set, says the same in words a person reads beside the board.
 */
export type ValueResult<T> =
  | { status: "valid"; value: T }
  | { status: "invalid"; message: string; forPeople?: string };

/** Each type of value a control, a key or a selection holds, as a sentence names it. */
export const INTERACTION_VALUE_TYPE_WORDS: Record<
  InteractionValueType["type"],
  string
> = {
  string: "text",
  number: "a number",
  integer: "a whole number",
  boolean: "true or false",
  instant: "a timestamp",
};

/** Each unit an instant's ticks count, as a sentence names it. */
export const TIME_UNIT_WORDS: Record<InteractionTimeUnit, string> = {
  s: "seconds",
  ms: "milliseconds",
  us: "microseconds",
  ns: "nanoseconds",
};

export const NANOSECONDS_PER_UNIT: Record<InteractionTimeUnit, bigint> = {
  s: 1_000_000_000n,
  ms: 1_000_000n,
  us: 1_000n,
  ns: 1n,
};

/** The instant's ticks as nanoseconds from its epoch. */
export function instantNanoseconds(
  value: Extract<InteractionValue, { type: "instant" }>,
): bigint {
  return value.ticks * NANOSECONDS_PER_UNIT[value.unit];
}

/**
 * The instant as a DuckDB timestamp expression over its ticks in its own
 * unit (`make_timestamp_ns`, `make_timestamp`, `make_timestamp_ms`), with
 * seconds written as microseconds. The ticks are written as counting from
 * the Unix epoch, which is where DuckDB's timestamps count from.
 */
export function instantSqlExpression(
  value: Pick<Extract<InteractionValue, { type: "instant" }>, "ticks" | "unit">,
): string {
  switch (value.unit) {
    case "ns":
      return `make_timestamp_ns(${value.ticks})`;
    case "us":
      return `make_timestamp(${value.ticks})`;
    case "ms":
      return `make_timestamp_ms(${value.ticks})`;
    case "s":
      return `make_timestamp(${value.ticks * 1_000_000n})`;
  }
}

/**
 * The value as JSON can carry it: an integer, an instant's ticks and a provenance offset are written as decimal
 * strings, since a JSON number cannot hold every `bigint`. Throws for a number that is not finite.
 */
export function serializeInteractionValue(
  value: InteractionValue,
): SerializedInteractionValue {
  if (value.type === "integer") {
    return { type: "integer", value: value.value.toString() };
  }
  if (value.type === "instant") {
    const { provenance, ...instant } = value;
    return {
      ...instant,
      ticks: value.ticks.toString(),
      ...(provenance === undefined
        ? {}
        : {
            provenance: {
              ...provenance,
              offsetNs: provenance.offsetNs.toString(),
            },
          }),
    };
  }
  if (value.type === "number" && !Number.isFinite(value.value)) {
    throw new Error("Interaction numbers must be finite.");
  }
  return { ...value };
}

/**
 * Reads a value from the form `serializeInteractionValue` writes. Invalid for any other shape, an unknown member,
 * a number that is not finite, or an integer or ticks string outside the one decimal spelling of its value.
 */
export function parseInteractionValue(
  input: unknown,
): ValueResult<InteractionValue> {
  const parsed = serializedInteractionValueSchema.safeParse(input);
  if (!parsed.success) {
    return {
      status: "invalid",
      message:
        "Expected a tagged value with integers and ticks written as canonical decimal strings.",
      forPeople: "The value is not written in a form the board reads.",
    };
  }
  const value = parsed.data;
  if (value.type === "integer") {
    return {
      status: "valid",
      value: { type: "integer", value: BigInt(value.value) },
    };
  }
  if (value.type === "instant") {
    const { provenance, ...instant } = value;
    return {
      status: "valid",
      value: {
        ...instant,
        ticks: BigInt(value.ticks),
        ...(provenance === undefined
          ? {}
          : {
              provenance: {
                ...provenance,
                offsetNs: BigInt(provenance.offsetNs),
              },
            }),
      },
    };
  }
  return { status: "valid", value };
}

/**
 * Converts a value to the target type only when nothing is lost: an integer becomes a number only within the
 * safe-integer range, a number becomes an integer only when it is a safe integer, and an instant keeps its epoch and
 * changes unit only when its ticks divide evenly. Null is valid only for a nullable target, and a value of any other
 * type than the target's is invalid.
 */
export function convertInteractionValue(
  value: InteractionValue,
  target: InteractionValueType,
): ValueResult<InteractionValue> {
  if (value.type === "null") {
    return target.nullable
      ? { status: "valid", value: { type: "null" } }
      : {
          status: "invalid",
          message: "This value type does not accept null.",
          forPeople:
            "The value is empty, and an empty value does not fit here.",
        };
  }
  if (value.type === "number" && !Number.isFinite(value.value)) {
    return {
      status: "invalid",
      message: "Interaction numbers must be finite.",
      forPeople: "The value is not a finite number.",
    };
  }
  if (value.type === "integer" && target.type === "number") {
    const number = Number(value.value);
    // A bigint outside the safe-integer range rounds to a number outside it.
    return Number.isSafeInteger(number)
      ? { status: "valid", value: { type: "number", value: number } }
      : {
          status: "invalid",
          message:
            "The integer is outside the safe-integer range, so it cannot become a number without losing precision.",
          forPeople: "The value is too large to keep exactly as a number.",
        };
  }
  if (value.type === "number" && target.type === "integer") {
    return Number.isSafeInteger(value.value)
      ? {
          status: "valid",
          value: { type: "integer", value: BigInt(value.value) },
        }
      : {
          status: "invalid",
          message:
            'An integer must be a safe JavaScript integer, or be sent as a decimal string ({"type": "integer", "value": "..."}).',
          forPeople:
            "Only a whole number fits here, and the value is not one or is too large to keep exactly.",
        };
  }
  if (value.type === "instant" && target.type === "instant") {
    if (value.epoch !== target.epoch) {
      return {
        status: "invalid",
        message: "The instants belong to different epochs.",
        forPeople:
          "The timestamp counts from a different starting time than the timestamps here.",
      };
    }
    const ns = instantNanoseconds(value);
    const divisor = NANOSECONDS_PER_UNIT[target.unit];
    if (ns % divisor !== 0n) {
      return {
        status: "invalid",
        message: `The instant cannot be written in ${target.unit} without losing precision.`,
        forPeople: `The timestamp is more precise than the whole ${TIME_UNIT_WORDS[target.unit]} the timestamps here are kept in.`,
      };
    }
    return {
      status: "valid",
      value: { ...value, ticks: ns / divisor, unit: target.unit },
    };
  }
  if (value.type !== target.type) {
    return {
      status: "invalid",
      message: `A value of type ${value.type} cannot be used as ${target.type}.`,
      forPeople: `The value is ${INTERACTION_VALUE_TYPE_WORDS[value.type]}, and only ${INTERACTION_VALUE_TYPE_WORDS[target.type]} fits here.`,
    };
  }
  return { status: "valid", value: { ...value } };
}

/**
 * A text to compare or index values by. Two instants of one epoch that mark the same nanosecond share a key
 * whatever their units and provenance; values of different types never share one.
 */
export function interactionValueKey(value: InteractionValue): string {
  if (value.type === "instant") {
    return JSON.stringify([
      "instant",
      value.epoch,
      instantNanoseconds(value).toString(),
    ]);
  }
  // Listing the members fixes their order in the text, so equal values written
  // with their members in another order share a key.
  return JSON.stringify(serializeInteractionValue(value), ["type", "value"]);
}

/**
 * Orders two numbers, two integers, or two instants of one epoch: -1 when `left` is lower, 1 when it is higher,
 * 0 when they are equal. Instants are compared in nanoseconds, so their units may differ. Invalid for any other pair
 * and for a number that is not finite.
 */
export function compareInteractionValues(
  left: InteractionValue,
  right: InteractionValue,
): ValueResult<number> {
  let a: number | bigint;
  let b: number | bigint;
  if (left.type === "number" && right.type === "number") {
    if (!Number.isFinite(left.value) || !Number.isFinite(right.value)) {
      return {
        status: "invalid",
        message: "Compared numbers must be finite.",
        forPeople: "These values can't be put in order.",
      };
    }
    a = left.value;
    b = right.value;
  } else if (left.type === "integer" && right.type === "integer") {
    a = left.value;
    b = right.value;
  } else if (
    left.type === "instant" &&
    right.type === "instant" &&
    left.epoch === right.epoch
  ) {
    a = instantNanoseconds(left);
    b = instantNanoseconds(right);
  } else {
    return {
      status: "invalid",
      message:
        "Only two numbers, two integers, or two instants with the same epoch can be compared.",
      forPeople: "These values can't be put in order.",
    };
  }
  if (a === b) {
    return { status: "valid", value: 0 };
  }
  return { status: "valid", value: a < b ? -1 : 1 };
}

/** One value as the text of a control's URL parameter: its JSON tagged value. */
export function encodeInteractionValue(value: InteractionValue): string {
  return JSON.stringify(serializeInteractionValue(value));
}

/**
 * A control's values as the texts of its URL parameter, one JSON tagged value each. `undefined`, for a control left
 * at its default, writes no parameter. An empty list writes one empty text, so a link can carry a control set to no
 * values.
 */
export function encodeInteractionOverride(
  values: readonly InteractionValue[] | undefined,
): string[] | undefined {
  if (values?.length === 0) {
    return [""];
  }
  return values?.map(encodeInteractionValue);
}

/**
 * Reads the texts `encodeInteractionOverride` writes, converting each value to the control's type (`target`). A
 * control that holds one value (`multi` false) reads the first text and ignores the rest, as a field control does.
 * Invalid when a text it reads is not a JSON tagged value or its value does not convert.
 */
export function decodeInteractionOverride(
  texts: readonly string[] | undefined,
  target: InteractionValueType,
  multi: boolean,
): ValueResult<InteractionValue[] | undefined> {
  if (texts === undefined) {
    return { status: "valid", value: undefined };
  }
  const read = multi ? texts : texts.slice(0, 1);
  if (read.length === 1 && read[0] === "") {
    return { status: "valid", value: [] };
  }
  const values: InteractionValue[] = [];
  for (const text of read) {
    let raw: unknown;
    try {
      raw = JSON.parse(text);
    } catch {
      return {
        status: "invalid",
        message: "The link does not contain a valid tagged value.",
      };
    }
    const parsed = parseInteractionValue(raw);
    if (parsed.status === "invalid") {
      return parsed;
    }
    const converted = convertInteractionValue(parsed.value, target);
    if (converted.status === "invalid") {
      return converted;
    }
    values.push(converted.value);
  }
  return { status: "valid", value: values };
}
